import { Injectable, inject } from '@angular/core';

import { CapicheLoggerService } from 'capiche-logger';
import { ApiService } from 'capiche-api';
import { UserService } from 'capiche-auth';

import { editStateEnum, paragraphDTO } from '@capiche/ts-data-models-shared';
import { clientUserDictionaryEntry } from '@capiche/ts-data-models-client';

import { CAPICHE_TEXT_PROCESSOR_CONFIG } from '../capiche-text-processor.config';

@Injectable({
  providedIn: 'root',
})
export class SuggestionRepository extends ApiService {
  private readonly config = inject(CAPICHE_TEXT_PROCESSOR_CONFIG);
  private capicheApiBaseUrl = this.config.baseApiUrl;
  private suggestionsEndpoint = `${this.capicheApiBaseUrl}/v1/getSuggestions`;
  private enrichSuggestionsEndpoint = `${this.capicheApiBaseUrl}/v1/enrichSuggestions`;
  private userDictionaryEndpoint = `${this.capicheApiBaseUrl}/v1/userDictionary`;

  override logger: CapicheLoggerService = new CapicheLoggerService('suggestionRepository:', false);

  constructor(userService: UserService) {
    super(userService);
    this.logger.log('constructor');
    this.logger.log('userService:', userService);
    this.logger.log('capicheApiBaseUrl:', this.capicheApiBaseUrl);
    this.logger.log('suggestionsEndpoint:', this.suggestionsEndpoint);
    this.logger.log('userDictionaryEndpoint:', this.userDictionaryEndpoint);
  }

  /**
   * Sends a paragraphDTO to the suggestion service and returns an enriched paragraph with token-level suggestions.
   *
   * @param input - A paragraphDTO containing the full input string, its character bounds (index, length),
   *                and an optional array of sentences (each with optional tokens).
   *                If sentences or tokens are omitted, the backend will tokenize and process the entire paragraph.
   *
   * @returns Promise<paragraphDTO> - The updated paragraphDTO including suggestions for the relevant tokens.
   *
   * Note: If the request fails, returns a fallback paragraphDTO with `state: 'invalid'`.
   * TODO: test this funciton with a non-empty tokens array
   */
  getSuggestions(input: paragraphDTO): Promise<paragraphDTO> {
    return new Promise<paragraphDTO>((resolve, reject) => {
      this.post(this.suggestionsEndpoint, input)
        .then((response) => {
          if (response == null) {
            throw new Error('Invalid response returned by endpoint');
          }
          this.logger.log('getSuggestions: response:', response);
          resolve(response.body as paragraphDTO);
        })
        .catch((error) => {
          this.logger.error('error fetching suggestions:', error);
          resolve({ input: '', index: 0, length: 0, editState: editStateEnum.invalid, sentences: [] }); // Resolve the Promise with null in case of an error
        });
    });
  }

  /**
   * Phase-2: sends the dict-checked paragraphDTO to /enrichSuggestions and returns
   * a GPT-enriched paragraphDTO. The client should call this after getSuggestions()
   * resolves and update the UI a second time with the richer suggestions.
   *
   * @param input - A paragraphDTO with editState=checked tokens from getSuggestions()
   * @returns Promise<paragraphDTO> with GPT-enriched suggestions merged in
   */
  enrichSuggestions(input: paragraphDTO): Promise<paragraphDTO> {
    return new Promise<paragraphDTO>((resolve) => {
      this.post(this.enrichSuggestionsEndpoint, input)
        .then((response) => {
          if (response == null) {
            throw new Error('Invalid response returned by enrichSuggestions endpoint');
          }
          this.logger.log('enrichSuggestions: response:', response);
          resolve(response.body as paragraphDTO);
        })
        .catch((error) => {
          this.logger.error('error fetching enriched suggestions:', error);
          resolve({ input: '', index: 0, length: 0, editState: editStateEnum.invalid, sentences: [] });
        });
    });
  }

  getAllStoredLookups(): Promise<clientUserDictionaryEntry[]> {
    return new Promise<clientUserDictionaryEntry[]>((resolve, reject) => {
      this.get(`${this.userDictionaryEndpoint}/${this.currentUser?.uid}`)
        .then((response) => {
          if (response == null) {
            throw new Error('Invalid response returned by endpoint');
          }
          resolve(
            response.body.map((wordData: clientUserDictionaryEntry) => {
              this.logger.log('wordData:', wordData); // lookupword and suggestions
              // TODO: should this not be { ...wordData } ? possibly coercing  to ensure suggestions is always defined
              return {
                lookupWord: wordData.lookupWord,
                spellingState: wordData.spellingState,
                suggestions: wordData.suggestions ?? [],
              };
            }),
          );
        })
        .catch((error) => {
          this.logger.error('Error fetching userDictionary:', error);
          resolve([]); // Resolve the Promise with null in case of an error
        });
    });
  }
}
